"use client"

import { postContactNewsLetter } from "@/api/generated/BMWApi"
import { getUtmParamsAsKeyValueList } from "@/lib/common/utmParams"
import colors from "@/lib/theme/colors"
import { theme } from "@/lib/theme/theme"
import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, TextField, Typography, useMediaQuery } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { LegacyRef, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import * as yup from "yup"
import {
  NEWSLETTER_DUPLICATE_ERROR,
  NEWSLETTER_EMAIL_ERROR,
  NEWSLETTER_EMAIL_PLACEHOLDER,
  NEWSLETTER_GENERAL_ERROR,
  NEWSLETTER_LEGAL_LINK_TEXT,
  NEWSLETTER_LEGAL_PREFIX,
  NEWSLETTER_LEGAL_SUFFIX,
  NEWSLETTER_PRIVACY_URL,
  NEWSLETTER_SUBMIT_LABEL,
} from "./newsletterContent"

// Dot access so Next.js inlines the value into the client bundle.
// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY

const emailRegex =
  // eslint-disable-next-line max-len
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const schema = yup.object({
  email: yup.string().matches(emailRegex, NEWSLETTER_EMAIL_ERROR).required(NEWSLETTER_EMAIL_ERROR),
})

type NewsletterFormInput = yup.InferType<typeof schema>

const StyledForm = styled.form<{ popup: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    gap: ${(p) => (p.popup ? "16px" : "24px")};
  }
`

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 0;
    align-items: flex-start;
    /* Field and button read as one joined bar, so the button loses its outer corners. */
    .MuiButton-root {
      border-radius: 6px 0 0 0;
    }
  }
`

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "popup",
})<{ popup: boolean }>`
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 26px 0 24px;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 0;
    margin-bottom: ${(p) => (p.popup ? "14px" : "0")};
  }
  .MuiInputBase-root {
    min-height: 50px;
    background-color: ${colors.directDisable};
    border-radius: 0;
    &::before,
    &::after {
      display: none;
    }
  }
  .MuiInputBase-input {
    padding: 13px 10px;
    font-size: 16px;
    text-align: right;
    color: ${colors.directBlack};
    &::placeholder {
      color: ${colors.directBlack};
      opacity: 1;
    }
  }
  .MuiInputBase-root.Mui-error {
    border-bottom: 1px solid ${colors.directLightRed};
  }
  .MuiInputBase-root.Mui-focused {
    background-color: ${colors.directDisable};
  }
  .MuiFormHelperText-root {
    top: unset;
    bottom: 0;
    ${(p) => p.theme.breakpoints.up("md")} {
      bottom: -20px;
    }
    &.Mui-error {
      color: ${colors.directLightRed};
    }
  }
`

const SubmitButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "popup",
})<{ popup: boolean }>`
  height: 50px;
  padding: 0 24px;
  ${({ popup }) =>
    !popup &&
    `
    &.MuiButtonBase-root.Mui-disabled {
      background-color: ${colors.directAzure};
      color: ${colors.directBlack};
    }
  `}
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-shrink: 0;
  }
`

const ErrorText = styled(Typography)`
  color: ${colors.directLightRed};
  font-size: 14px;
  text-align: right;
`

const LegalText = styled(Typography)`
  color: ${colors.white};
  font-size: 14px;
  line-height: normal;
  text-align: right;
  letter-spacing: unset;
  a {
    color: #0099ff;
    text-decoration: none;
  }
`

interface NewsletterFormProps {
  source: string
  onSuccess: () => void
  layout?: "footer" | "popup"
  submitLabel?: string
}

const NewsletterForm = ({
  source,
  onSuccess,
  layout = "footer",
  submitLabel = NEWSLETTER_SUBMIT_LABEL,
}: NewsletterFormProps): React.ReactElement => {
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"))
  const platform = isDesktop ? "Pc" : isTablet ? "Tablet" : "Mobile"
  const reCaptchaRef = useRef<ReCAPTCHA>()
  const [apiError, setApiError] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
  } = useForm<NewsletterFormInput>({
    defaultValues: { email: "" },
    resolver: yupResolver(schema),
    mode: "onChange",
  })

  const { mutate, isPending } = useMutation({
    mutationKey: ["postNewsLetter", source],
    mutationFn: postContactNewsLetter,
    onSuccess: (res) => {
      // Convention across this codebase: a falsy errorCode means success.
      if (!res.data?.errorCode) {
        onSuccess()
        return
      }
      setApiError(res.data.errorMessage || NEWSLETTER_DUPLICATE_ERROR)
    },
    onError: () => {
      setApiError(NEWSLETTER_GENERAL_ERROR)
    },
  })

  const onSubmit: SubmitHandler<NewsletterFormInput> = async (data) => {
    setApiError(null)
    let gRecaptchaResponse = ""
    if (RECAPTCHA_KEY && reCaptchaRef.current) {
      reCaptchaRef.current.reset()
      gRecaptchaResponse = (await reCaptchaRef.current.executeAsync()) as string
    }
    mutate({
      emailAddress: data.email,
      platform,
      isMailing: true,
      gRecaptchaResponse,
      utmValues: getUtmParamsAsKeyValueList(),
      sid: "",
      formName: source,
    })
  }

  const isPopup = layout === "popup"

  const emailField = (
    <Controller
      name="email"
      control={control}
      render={({ field }) => (
        <StyledTextField
          {...field}
          type="email"
          variant="filled"
          hiddenLabel
          fullWidth
          placeholder={NEWSLETTER_EMAIL_PLACEHOLDER}
          error={!!errors.email && !!touchedFields.email}
          helperText={touchedFields.email ? errors.email?.message : undefined}
          inputProps={{ dir: "rtl", "aria-label": "דוא״ל" }}
          popup={isPopup}
        />
      )}
    />
  )

  const submitButton = (
    <SubmitButton
      type="submit"
      variant="contained"
      disabled={isPending || !isValid}
      fullWidth={isPopup}
      popup={isPopup}
    >
      {submitLabel}
    </SubmitButton>
  )

  const legal = (
    <LegalText variant="body2">
      {NEWSLETTER_LEGAL_PREFIX}
      <a href={NEWSLETTER_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
        {NEWSLETTER_LEGAL_LINK_TEXT}
      </a>
      {NEWSLETTER_LEGAL_SUFFIX}
    </LegalText>
  )

  return (
    <>
      <StyledForm onSubmit={handleSubmit(onSubmit)} noValidate popup={isPopup}>
        {isPopup ? (
          <>
            {emailField}
            {apiError && <ErrorText variant="body2">{apiError}</ErrorText>}
            {legal}
            {submitButton}
          </>
        ) : (
          <>
            <FieldRow>
              {emailField}
              {submitButton}
            </FieldRow>
            {apiError && <ErrorText variant="body2">{apiError}</ErrorText>}
            {legal}
          </>
        )}
      </StyledForm>
      {RECAPTCHA_KEY && (
        <ReCAPTCHA
          ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
          sitekey={RECAPTCHA_KEY}
          size="invisible"
          hl="iw"
          tabIndex={-1}
        />
      )}
    </>
  )
}

export default NewsletterForm
